import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, RotateCcw, HelpCircle, Trophy, Loader2, ArrowRight } from "lucide-react";
import BeeIcon from "@/components/BeeIcon";
import { generatePuzzle, sizeForLevel, REGION_COLORS } from "@/lib/beesweeper";

const MAX_LIVES = 3;
const PAD = 3;
const GAP = 2; // space between blocks (px)

export default function BeeSweeper() {
  const { user } = useAuth();

  const [loaded, setLoaded] = useState(false);
  const [level, setLevel] = useState(1);
  const [marks, setMarks] = useState([]);      // ["r-c"]
  const [revealed, setRevealed] = useState([]); // correctly revealed bees
  const [mistakes, setMistakes] = useState([]); // wrong reveals
  const [lives, setLives] = useState(MAX_LIVES);
  const [status, setStatus] = useState("playing"); // playing | won | lost
  const [wins, setWins] = useState(0);
  const [bestLevel, setBestLevel] = useState(1);
  const [showHelp, setShowHelp] = useState(false);

  const puzzle = useMemo(() => generatePuzzle(level), [level]);
  const { N, region, solution } = puzzle;

  const marksSet = useMemo(() => new Set(marks), [marks]);
  const revealedSet = useMemo(() => new Set(revealed), [revealed]);
  const mistakesSet = useMemo(() => new Set(mistakes), [mistakes]);

  const cellPx = useMemo(() => Math.max(30, Math.min(64, Math.floor(560 / N))), [N]);
  const boardPx = cellPx * N + PAD * 2;

  /* ---------- load progress ---------- */
  useEffect(() => {
    let cancelled = false;
    api.get("/games/beesweeper")
      .then((r) => {
        if (cancelled) return;
        const d = r.data || {};
        setLevel(d.level || 1);
        setMarks(d.marks || []);
        setRevealed(d.revealed || []);
        setMistakes(d.mistakes || []);
        setLives(typeof d.lives === "number" ? d.lives : MAX_LIVES);
        setStatus(d.status || "playing");
        setWins(d.wins || 0);
        setBestLevel(d.best_level || d.level || 1);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  /* ---------- persist (debounced) ---------- */
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!loaded) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/games/beesweeper", {
        level, marks, revealed, mistakes, lives, status, wins,
        best_level: Math.max(bestLevel, level),
      }).catch(() => {});
    }, 500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [loaded, level, marks, revealed, mistakes, lives, status, wins, bestLevel]);

  /* ---------- interactions ---------- */
  const singleTap = useCallback((id) => {
    if (status !== "playing") return;
    if (revealedSet.has(id) || mistakesSet.has(id)) return;
    setMarks((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  }, [status, revealedSet, mistakesSet]);

  const doubleTap = useCallback((id) => {
    if (status !== "playing") return;
    if (revealedSet.has(id) || mistakesSet.has(id)) return;
    setMarks((m) => m.filter((x) => x !== id));
    if (solution.has(id)) {
      setRevealed((prev) => {
        const nx = [...prev, id];
        if (nx.length === N) {
          setStatus("won");
          setWins((w) => w + 1);
          setBestLevel((b) => Math.max(b, level + 1));
          toast.success("Hive solved! 🍯 All bees found.");
        }
        return nx;
      });
    } else {
      setMistakes((prev) => [...prev, id]);
      setLives((l) => {
        const nl = l - 1;
        if (nl <= 0) {
          setStatus("lost");
          toast.error("Out of bee lives! 🐝");
        } else {
          toast.warning(`No bee there — ${nl} bee ${nl === 1 ? "life" : "lives"} left.`);
        }
        return nl;
      });
    }
  }, [status, revealedSet, mistakesSet, solution, N, level]);

  // single vs double tap detection (works for mouse + touch).
  // A second tap within DOUBLE_MS of the first is treated as a double-tap
  // (reveal), even if the single-tap timer already fired — doubleTap() removes
  // any stray mark, so the reveal always wins. SINGLE_MS keeps mark feedback snappy.
  const SINGLE_MS = 250;
  const DOUBLE_MS = 400;
  const tapRef = useRef({}); // id -> { timer, ts }
  const handleTap = useCallback((id) => {
    const st = tapRef.current;
    const prev = st[id];
    const now = performance.now();
    if (prev && now - prev.ts < DOUBLE_MS) {
      if (prev.timer) clearTimeout(prev.timer);
      st[id] = null;
      doubleTap(id);
      return;
    }
    const timer = setTimeout(() => {
      const e = st[id];
      if (e) e.timer = null; // keep ts so a slightly-late second tap still counts as a double
      singleTap(id);
    }, SINGLE_MS);
    st[id] = { timer, ts: now };
  }, [singleTap, doubleTap]);

  /* ---------- level controls ---------- */
  const resetLevel = useCallback((newLevel) => {
    setMarks([]); setRevealed([]); setMistakes([]); setLives(MAX_LIVES); setStatus("playing");
    if (newLevel != null) setLevel(newLevel);
  }, []);

  const nextLevel = useCallback(() => resetLevel(level + 1), [level, resetLevel]);
  const prevLevel = useCallback(() => { if (level > 1) resetLevel(level - 1); }, [level, resetLevel]);

  if (user && !user.is_admin) return <Navigate to="/app" replace />;

  const cells = [];
  for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) cells.push({ r, c, id: `${r}-${c}` });

  const iconSize = Math.round(cellPx * 0.78);
  const cellRadius = Math.max(4, Math.round(cellPx * 0.2));

  return (
    <div className="space-y-5" data-testid="beesweeper-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/app/games" className="inline-flex items-center gap-1 text-sm text-amber-700 hover:text-amber-900 mb-1" data-testid="back-to-games">
            <ArrowLeft className="w-4 h-4" /> Games
          </Link>
          <h1 className="font-display text-3xl font-semibold tracking-tight flex items-center gap-2">
            <BeeIcon size={30} /> Bee Hero
          </h1>
          <p className="text-stone-500 mt-1 text-sm">
            Save the bees from being destroyed — find them all before all is lost!
          </p>
        </div>
        <button onClick={() => setShowHelp((v) => !v)} className="inline-flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-900" data-testid="beesweeper-help-toggle">
          <HelpCircle className="w-4 h-4" /> How to play
        </button>
      </div>

      {showHelp && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5 text-sm text-stone-700 space-y-2" data-testid="beesweeper-help">
          <ul className="list-disc pl-5 space-y-1">
            <li>The grid is split into <strong>colored regions</strong>. Exactly <strong>one bee</strong> hides in each region.</li>
            <li>There is also only <strong>one bee per row and per column</strong>, and <strong>no two bees touch</strong> (not even diagonally).</li>
            <li><strong>Single tap</strong> a cell to mark it with an ✕ (your note that no bee is there).</li>
            <li><strong>Double tap</strong> to reveal a bee. Right cell → 🐝 appears. Wrong cell → you lose a <strong>bee life</strong>.</li>
            <li>You have <strong>{MAX_LIVES} bee lives</strong>. Reveal all the bees before they run out to win and advance a level.</li>
            <li>Grids start at 5×5 and grow every 25 levels up to 15×15. Your progress is saved automatically.</li>
          </ul>
        </div>
      )}

      {/* Status bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="rounded-2xl bg-white border border-stone-200 px-4 py-2.5" data-testid="level-indicator">
          <div className="text-[11px] font-medium uppercase tracking-wide text-stone-400">Level</div>
          <div className="text-2xl font-bold tabular-nums leading-none text-stone-800">{level}</div>
        </div>
        <div className="rounded-2xl bg-white border border-stone-200 px-4 py-2.5">
          <div className="text-[11px] font-medium uppercase tracking-wide text-stone-400">Grid</div>
          <div className="text-2xl font-bold tabular-nums leading-none text-stone-800">{N}×{N}</div>
        </div>
        <div className="rounded-2xl bg-white border border-stone-200 px-4 py-2.5">
          <div className="text-[11px] font-medium uppercase tracking-wide text-stone-400">Bees</div>
          <div className="text-2xl font-bold tabular-nums leading-none text-stone-800" data-testid="bees-found">{revealed.length}/{N}</div>
        </div>
        {/* Bee lives */}
        <div className="rounded-2xl bg-white border border-stone-200 px-4 py-2.5" data-testid="bee-lives">
          <div className="text-[11px] font-medium uppercase tracking-wide text-stone-400">Bee lives</div>
          <div className="flex items-center gap-1 mt-0.5">
            {Array.from({ length: MAX_LIVES }).map((_, i) => (
              <BeeIcon key={i} size={22} dim={i >= lives} />
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {/* Board */}
          <div className="relative overflow-x-auto pb-2">
            {!loaded && (
              <div className="absolute inset-0 z-20 grid place-items-center bg-white/60 backdrop-blur-sm rounded-xl">
                <div className="flex items-center gap-2 text-stone-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              </div>
            )}
            <div className="relative" style={{ width: boardPx, height: boardPx, touchAction: "manipulation" }} data-testid="beesweeper-board">
              {/* Background grid: rounded, spaced region blocks (no borders) */}
              <svg width={boardPx} height={boardPx} className="absolute inset-0" style={{ pointerEvents: "none" }} aria-hidden="true">
                {cells.map(({ r, c }) => (
                  <rect
                    key={`f-${r}-${c}`}
                    x={PAD + c * cellPx + GAP / 2}
                    y={PAD + r * cellPx + GAP / 2}
                    width={cellPx - GAP}
                    height={cellPx - GAP}
                    rx={cellRadius}
                    ry={cellRadius}
                    fill={REGION_COLORS[region[r][c] % REGION_COLORS.length]}
                  />
                ))}
              </svg>

              {/* Interactive cell layer */}
              {cells.map(({ r, c, id }) => {
                const isRevealed = revealedSet.has(id);
                const isMistake = mistakesSet.has(id);
                const isMarked = marksSet.has(id);
                const showLostBee = status === "lost" && solution.has(id) && !isRevealed;
                return (
                  <button
                    key={id}
                    data-testid={`cell-${id}`}
                    data-bee={solution.has(id) ? "1" : "0"}
                    onClick={() => handleTap(id)}
                    disabled={status !== "playing"}
                    className="absolute grid place-items-center focus:outline-none"
                    style={{
                      left: PAD + c * cellPx, top: PAD + r * cellPx, width: cellPx, height: cellPx,
                      background: "transparent", border: "none",
                      cursor: status === "playing" ? "pointer" : "default",
                      touchAction: "manipulation",
                    }}
                  >
                    {isRevealed && <BeeIcon size={iconSize} />}
                    {isMistake && <span style={{ fontSize: cellPx * 0.6, lineHeight: 1, color: "#dc2626", fontWeight: 800 }}>✕</span>}
                    {showLostBee && <BeeIcon size={iconSize} dim />}
                    {isMarked && !isRevealed && !isMistake && (
                      <span style={{ fontSize: cellPx * 0.5, lineHeight: 1, color: "rgba(120,53,15,0.55)", fontWeight: 700 }}>✕</span>
                    )}
                  </button>
                );
              })}

              {/* overlays */}
              {(status === "won" || status === "lost") && (
                <div className="absolute inset-0 z-10 grid place-items-center rounded-xl bg-white/85 backdrop-blur-sm" data-testid="beesweeper-overlay">
                  <div className="text-center px-6 py-5 rounded-2xl bg-white border border-amber-200 shadow-lg max-w-xs">
                    <div className="mb-1 flex justify-center"><BeeIcon size={48} dim={status === "lost"} /></div>
                    <div className="font-display text-2xl font-semibold text-stone-800">{status === "won" ? "Hive solved!" : "Out of bee lives"}</div>
                    <div className="text-sm text-stone-500 mt-1">
                      {status === "won" ? `You found all ${N} bees on level ${level}.` : "The hidden bees are shown in gray."}
                    </div>
                    {status === "won" ? (
                      <Button onClick={nextLevel} className="mt-4 rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="next-level-button">
                        Next level <ArrowRight className="w-4 h-4 ml-1.5" />
                      </Button>
                    ) : (
                      <Button onClick={() => resetLevel()} className="mt-4 rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="retry-level-button">
                        <RotateCcw className="w-4 h-4 mr-1.5" /> Try again
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => resetLevel()} variant="outline" className="rounded-full" data-testid="restart-level-button" disabled={status !== "playing"}>
              <RotateCcw className="w-4 h-4 mr-1.5" /> Restart level
            </Button>
            <Button onClick={prevLevel} variant="outline" className="rounded-full" data-testid="prev-level-button" disabled={level <= 1}>
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Previous
            </Button>
            {level < bestLevel && (
              <Button onClick={nextLevel} variant="outline" className="rounded-full" data-testid="skip-level-button">
                Next <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Side panel */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-stone-200 bg-white p-5">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-amber-500" /> Your progress</h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-stone-500">Current level</dt><dd className="font-semibold text-stone-800">{level}</dd></div>
              <div className="flex justify-between"><dt className="text-stone-500">Highest reached</dt><dd className="font-semibold text-stone-800">{bestLevel}</dd></div>
              <div className="flex justify-between"><dt className="text-stone-500">Levels solved</dt><dd className="font-semibold text-stone-800">{wins}</dd></div>
              <div className="flex justify-between"><dt className="text-stone-500">Grid at level {level}</dt><dd className="font-semibold text-stone-800">{sizeForLevel(level)}×{sizeForLevel(level)}</dd></div>
            </dl>
            <p className="text-xs text-stone-400 mt-4">Progress saves automatically — come back anytime and pick up where you left off.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
